import * as React from 'react';
import { LngLatBounds, Map } from 'mapbox-gl';
import bbox from '@turf/bbox';
import { saveAs } from 'file-saver';

import { useToast } from '@longline/aqua-ui/services/Toast/useToast';

import { GlobalStateContext, TMode, IOrganixRun } from './GlobalStateContext';
import { ILayer } from '../../types/ILayer';
import { ExcelImport } from '../../dialogs/ExcelImport';
import { LayerUtils } from '../../util/LayerUtils';
import { IStructure } from '../../types/IStructure';
import { IPoint } from '../../types/IPoint';
import { PolygonUtils } from '../../util/PolygonUtils';
import { EditStructureDialog } from '../../dialogs/EditStructure/EditStructureDialog';
import { DefaultReference, IReference } from '../../types/IReference';
import { ReferenceImport } from '../../dialogs/ReferenceImport/ReferenceImport';
import { DefaultProject, IProject, speciesDefaultsFor } from '../../types/IProject';
import { LoadProjectDialog } from '../../dialogs/LoadProject/LoadProjectDialog';
import { IDataPoint } from '../../types/IDataPoint';
import { IRect } from '../../types/IRect';
import { IArea } from '../../types/IArea';
import { IFinsFile } from '../../types/IFinsFile';
import { IDriverset, ISystemLayer } from '../../api/models';
import { ELayerType } from '../../types/ELayerType';
import { TResourceState } from '../../types/TResourceState';
import { ISpecies } from '../../api/models/ISpecies';
import { useAuth } from '../auth/useAuth';
import { SpeciesApi } from '../../api/services/SpeciesApi';
import { SystemApi } from '../../api/services/SystemApi';
import { MAX_STRUCTURE_RADIUS } from '../../types/Constants';
import { AreaGridUtils } from '../../util/AreaGridUtils';
import { layerGroupInfoFor } from '../../types/ELayerGroup';

interface IProps {
  children?: React.ReactNode;
}

const GlobalState = (props: IProps) => {
  const auth = useAuth();
  const toaster = useToast();
  
  const map = React.useRef<Map | null>(null);

  // Keep track of previous authenticated state:
  const wasAuthenticated = React.useRef(auth.isAuthenticated);
  
  const [mode, setMode] = React.useState<TMode>(null);
  const [expanded, setExpanded] = React.useState(false);
  const [guide, setGuide] = React.useState(true);

  // Project
  const [project, setProject] = React.useState<IProject>(DefaultProject);
  const [loadingProject, setLoadingProject] = React.useState(false);

  // Layers
  const [importingLayer, setImportingLayer] = React.useState(false);
  const [layers, setLayers] = React.useState<ILayer[]>([]);
  const [activeLayer, setActiveLayer] = React.useState<ILayer|null>(null);

  // Structures
  const [showStructures, setShowStructures] = React.useState(true);
  const [structures, setStructures] = React.useState<IStructure[]>([]);
  const [selectedStructures, setSelectedStructures] = React.useState<IStructure[]>([]);
  const [editingStructure, setEditingStructure] = React.useState<IStructure|null>(null);

  // References
  const [referenceImporting, setReferenceImporting] = React.useState(false);
  const [references, setReferences] = React.useState<IReference[]>([]);
  const [selectedReference, setSelectedReference] = React.useState<IReference|null>(null);

  // Areas
  const [areas, setAreas] = React.useState<IArea[]>([]);
  const [selectedArea, setSelectedArea] = React.useState<IArea|null>(null);
  const [organix, setOrganix] = React.useState<IOrganixRun|null>(null);
  
  // Driver sets belonging to the currently-loaded system. Mirror only —
  // areas snapshot their own driverset when created. Empty when no system.
  const [driversets, setDriversets] = React.useState<IDriverset[]>([]);

  const setMap = (_map: Map) => {
    map.current = _map;
  }

  const getMap = () => map.current;

  const startImport = () => {
    setImportingLayer(true);
  }

  const handleImport = (layers: ILayer[]) => {
    setImportingLayer(false);
    addLayers(layers);
  }

  const addLayers = (newLayers: ILayer[], clear?: boolean) => {
    setLayers(sortLayers([...(clear ? [] : layers), ...newLayers ]))
    // Focus on first new layer.
    if(newLayers.length > 0) focusLayer(newLayers[0]);       
  }

  /**
   * Add the layers a completed simulation produced, stamped with a new run.
   *
   * Earlier results are hidden rather than removed. Two layers of the same type
   * carry the same zIndex, so leaving both visible makes it undefined which one
   * the map is actually drawing — the ambiguity is on the map, not just in the
   * list. They stay available to switch back on, and to compare.
   */
  const addResultLayers = (newLayers: ILayer[], areaName: string) => {
    const index = layers.reduce((highest, l) => Math.max(highest, l.run?.index ?? 0), 0) + 1;
    const run = { id: `run-${Date.now()}-${index}`, index, area: areaName, at: new Date().toISOString() };
    newLayers.forEach(l => l.run = run);

    // Mutated rather than replaced, like toggleLayer: activeLayer holds a
    // reference, and swapping objects out from under it would strand it.
    layers.forEach(l => { if (l.run) l.visible = false; });

    setLayers(sortLayers([...layers, ...newLayers]));
    if (newLayers.length > 0) focusLayer(newLayers[0]);
  }

  /** Remove every layer belonging to one run. */
  const deleteRun = (runId: string) => {
    setLayers([...layers.filter(l => l.run?.id !== runId)]);
  }

  const toggleLayer = (layer: ILayer) => {
    // Toggle argument layer's visibility:
    layer.visible = !layer.visible;
    // Make a new array of layers, so that components will update.
    setLayers([ ...layers]);
  }

  const deleteLayer = (layer: ILayer) => {
    // Remove layer from array, creating new array so that components will update.
    setLayers([ ...layers.filter(a => a != layer)]);
  }

  const focusLayer = (layer: ILayer) => {
    if(!map.current) return;
    const minLng = Math.min(...layer.data.map(p => p.lng));
    const maxLng = Math.max(...layer.data.map(p => p.lng));
    const minLat = Math.min(...layer.data.map(p => p.lat));
    const maxLat = Math.max(...layer.data.map(p => p.lat));
    map.current.fitBounds([
      [ minLng, minLat ],
      [ maxLng, maxLat ]
    ]);
  }
  
  // Update a single layer.
  const updateLayer = (layer: ILayer) => {
    // Update by creating new array of layers so that components update.
    setLayers(sortLayers([ ...layers.filter(a => a != layer), layer]));
  }

  /**
   * Run first, then group, then name. The Name column is not sortable, so this
   * ordering is the only one the user ever sees.
   *
   * Input layers have no run and sort to the top, since they are the base data
   * every result sits on. Results then come newest run first, so what SIMULATE
   * just produced is at the top of the list rather than buried under older
   * runs. Within a run, grouping keeps each model's output contiguous — by
   * name alone, a diagenesis run put "Bacteria in sediment" above the
   * deposition layers and stranded the other six sediment layers below them.
   */
  const runRank = (layer: ILayer): number =>
    layer.run ? -layer.run.index : Number.NEGATIVE_INFINITY;

  const sortLayers = (layers: ILayer[]): ILayer[] => {
    return layers.sort((a, b) => {
      const byRun = runRank(a) - runRank(b);
      if (byRun !== 0) return byRun;
      const byGroup = layerGroupInfoFor(a.type).order - layerGroupInfoFor(b.type).order;
      if (byGroup !== 0) return byGroup;
      const aName = `${LayerUtils.layerTypeToDefinition(a.type).name} (${a.source})`;
      const bName = `${LayerUtils.layerTypeToDefinition(b.type).name} (${b.source})`;
      return aName.localeCompare(bName);
    });
  }

  const clearLayers = () => {
    setActiveLayer(null);
    setLayers([]);
  }

  const setPaneExpansion = (_expanded: boolean) => setExpanded(_expanded);

  const togglePaneExpansion = () => setExpanded(!expanded);

  const clearStructures = () => {
    setSelectedStructures([]);
    setStructures([]);
  }

  // A trick is used when pasting a structure. The currently-selected
  // structure must first be properly unmounted. The unmounting is
  // detected using "pastable". After this, the new structure is added
  // and selected.
  const [pastable, setPastable] = React.useState<IStructure|null>(null);
  React.useEffect(() => {
    if(pastable) {
      pastable.name = `Structure ${structures.length+1}`;
      setStructures([ ...structures, pastable ]);
      setSelectedStructures([pastable]);
      setMode(null);
      setPastable(null);
    }
  }, [pastable]);
  const addStructure = (structure: IStructure) => {
    setSelectedStructures([]);
    setPastable(structure);
  }

  const addStructureCircle = (point: IPoint, radius: number) => {
    const structure: IStructure = {
      name: `Structure ${structures.length+1}`,
      type: 'circle',
      point: point,
      radius: Math.min(50, radius),
      stocking: project.stocking
    };
    setStructures([ ...structures, structure ]);
    setSelectedStructures([structure]);
    setMode(null);
  }

  const updateSelectedStructureCircle = (point: IPoint, radius: number) => {
    if (selectedStructures.length === 0) return;
    const activeStructure = selectedStructures[0];
    if (activeStructure.type !== 'circle') return;
    activeStructure.point = point;
    activeStructure.radius = Math.min(50, radius);
    setStructures([...structures]);
  }

  const addStructurePolygon = (points: IPoint[]) => {
    const structure: IStructure = {
      name: `Structure ${structures.length+1}`,
      type: 'polygon',
      points: points,
      stocking: project.stocking
    };
    setStructures([ ...structures, structure ]);
    setSelectedStructures([structure]);
    setMode(null);
  }

  const updateSelectedStructurePolygon = (points: IPoint[]) => {
    if (selectedStructures.length === 0) return;
    const activeStructure = selectedStructures[0];
    if (activeStructure.type !== 'polygon') return;
    activeStructure.points = points;
    setStructures([...structures]);
  }

  // Multi-select structure methods
  const toggleStructureSelection = (structure: IStructure, additive: boolean) => {
    if (additive) {
      // Ctrl+click: toggle the structure in/out of the selection
      if (selectedStructures.includes(structure)) {
        setSelectedStructures(selectedStructures.filter(s => s !== structure));
      } else {
        setSelectedStructures([...selectedStructures, structure]);
      }
    } else {
      // Regular click: replace the selection with just this structure
      setSelectedStructures([structure]);
    }
  }

  const selectAllStructures = () => {
    setSelectedStructures([...structures]);
  }

  const clearStructureSelection = () => {
    setSelectedStructures([]);
  }

  const deleteSelectedStructures = () => {
    setStructures([...structures.filter(s => !selectedStructures.includes(s))]);
    setSelectedStructures([]);
  }

  const moveSelectedStructures = (dLng: number, dLat: number) => {
    for (const structure of selectedStructures) {
      if (structure.type === 'circle') {
        structure.point = {
          lng: structure.point.lng + dLng,
          lat: structure.point.lat + dLat
        };
      } else if (structure.type === 'polygon') {
        structure.points = structure.points.map(p => ({
          lng: p.lng + dLng,
          lat: p.lat + dLat
        }));
      }
    }
    setStructures([...structures]);
  }

  const focusStructure = (structure: IStructure) => {
    if(!map.current) return;
    if(structure.type == 'circle') {
      // Create bbox for circle, with 3x padding around it.
      const bounds = new LngLatBounds(
        PolygonUtils.addMeters(structure.point, -structure.radius * 3, -structure.radius * 3),
        PolygonUtils.addMeters(structure.point,  structure.radius * 3,  structure.radius * 3)
      );
      map.current.fitBounds(bounds);
    } else {
      // Get bbox for polygon, and fly to it.
      map.current.fitBounds(PolygonUtils.getBbox(structure.points));
    }
  }

  const updateSelectedStructure = (_structure: IStructure) => {
    if(!editingStructure) return;
    editingStructure.name = _structure.name;
    editingStructure.stocking = _structure.stocking;
    if (editingStructure.type === 'circle' && _structure.type === 'circle') {
      editingStructure.radius = Math.min(MAX_STRUCTURE_RADIUS, _structure.radius);
    }
    setStructures([...structures]);
    setSelectedStructures([editingStructure]);
    setEditingStructure(null);
  }

  const startEditStructure = () => {
    if (selectedStructures.length === 0) return;
    setEditingStructure(selectedStructures[0]);
    // Disable editors, since they will consume keyboard events:
    setSelectedStructures([]);
  }

  const cancelEditStructure = () => {
    if(!editingStructure) return;
    setSelectedStructures([editingStructure]);
    setEditingStructure(null);
  }

  // REFERENCES

  const startReferenceImport = () => {
    setReferenceImporting(true);
  }

  const clearReferences = () => {
    setSelectedReference(null);
    setReferences([]);
  }

  const handleReferenceImport = (reference: IReference) => {
    setReferenceImporting(false);
    setReferences([ ...references, reference ]);
    focusReference(reference);
  }

  const updateReference = (reference: IReference) => {
    // Update by creating new array of layers so that components update.
    setReferences([ ...references.filter(a => a != reference), reference]);
  }

  const deleteReference = (reference: IReference) => {
    // Remove reference from array, creating new array so that components will update.
    setReferences([ ...references.filter(a => a != reference)]);
  }

  const focusReference = (reference: IReference) => {
    if(!map.current) return;
    if(!reference.geojson) return;
    const [ minLng, minLat, maxLng, maxLat ] = bbox(reference.geojson);
    map.current.fitBounds([
      [ minLng, minLat ],
      [ maxLng, maxLat ]
    ]);
  }

  const toggleReference = (reference: IReference) => {
    // Toggle argument references's visibility:
    reference.visible = !reference.visible;
    // Make a new array of references, so that components will update.
    setReferences([ ...references.filter(a => a != reference), reference]);
  }

  // PROJECT

  const startLoadProject = () => {
    setLoadingProject(true);
  }

  const loadProject = (json: any) => {
    setMode(null);
    setActiveLayer(null);
    setSelectedStructures([]);
    if(json.version == "3") {
      loadProjectV3(json);
    } else if(json.version == "2") {
      loadProjectV2(json);
    } else {
      loadProjectV1(json);
    }
  }

  const loadProjectV1 = (json: any) => {
    // Map location to zoom to:
    const latitude = json.Latitude;
    const longitude = json.Longitude;
    const zoom = json.Zoom;

    // Project info
    setProject({
      systemUUID: null,
      systemName: null,
      name: json.FarmName ?? DefaultProject.name,
      structureDepth: json.StructureDepth ?? DefaultProject.structureDepth,
      stocking: json.StockingDensity ?? DefaultProject.stocking,
      clipping: DefaultProject.clipping
    });

    // Layers:
    if(json.LayerProxies) {
      const layers = json.LayerProxies.map((p: any) => {
        const layer = LayerUtils.createLayer(
          p.DataSet.Type, 
          p.DataSet.Name, 
          p.DataSet.Data.map((d: any) => {
            const dp: IDataPoint = {
              lng: d.Longitude,
              lat: d.Latitude,
              value: [ d.U, d.V ],
            };
            return dp;
          })
        );
        layer.visible = p.DataSet.Visible != false;
        return layer;
      });
      addLayers(layers, true); // clear layers first
    }

    // References:
    if(json.ReferenceProxies) {
      setReferences(json.ReferenceProxies.map((p: any) => {
        const reference: IReference = { ...DefaultReference,
          name: p.Name,
          geojson: JSON.parse(p.Json),
          visible: p.Visible
        };
        return reference;
      }));
    }

    // Structures:
    if(json.StructureProxies) {
      setStructures(json.StructureProxies.map((p: any) => {
        const structure: IStructure = {
          type: 'circle',
          point: { lng: p.Longitude, lat: p.Latitude },
          radius: p.Radius,
          name: p.Name,
          stocking: p.StockingDensity
        };
        return structure;
      }
    ))}

    // Move map:
    map.current?.flyTo({
      center: [longitude, latitude],
      zoom: zoom
    });
  }

  /**
   * Load a project, fetching the referenced system's layers + driver sets if a
   * systemUUID is present. Aborts with a toast if the user lacks access.
   * Returns the loaded system's driver sets (or [] if no system).
   */
  const loadSystemAssets = async (
    systemUUID: string | null | undefined,
    extraFileLayers: ILayer[]
  ): Promise<{ layers: ILayer[]; driversets: IDriverset[]; ok: boolean }> => {
    let layers: ILayer[] = [];
    let driversets: IDriverset[] = [];
    if(systemUUID) {
      try {
        const system = await SystemApi.get(systemUUID);
        layers = system.layers.map(l => ({ ...LayerUtils.createLayer(l.type, l.name, l.data), visible: false }));
        const b = layers.find(l => l.type == ELayerType.Depth);
        if(b) b.visible = true;
        const v = layers.find(l => l.type == ELayerType.CurrentSpeed);
        if(v) v.visible = true;
        driversets = system.driversets || [];
      } catch {
        toaster.toast("This project references a system that you do not have access to. Project load aborted.", { dismissable: true });
        return { layers: [], driversets: [], ok: false };
      }
    }
    return { layers: [...layers, ...extraFileLayers], driversets, ok: true };
  }

  /**
   * Apply legacy area-shape defaults that older project files may be missing.
   */
  const applyAreaDefaults = (a: IArea) => {
    if(a.demThreshold === undefined) a.demThreshold = 1.0;
    if(a.days === undefined) a.days = 500;
    if(a.structureDepth === undefined) a.structureDepth = 6;
    if(a.faecesDiameter === undefined) a.faecesDiameter = 3.3;
    if(a.feedPelletDiameter === undefined) a.feedPelletDiameter = 4.5;
    if(a.xDispersion === undefined) a.xDispersion = 0.05;
    if(a.yDispersion === undefined) a.yDispersion = 0.05;
    if(a.solver === undefined) a.solver = 0;
    if(a.timestepReductionFactor === undefined) a.timestepReductionFactor = 1;
    // Older project files used `modelCode`; map it to the new simulationKind
    // and discard. We always assume 'deposition' since legacy projects only
    // had two options and Pathogens (modelCode 3) was never actually selectable.
    if(a.simulationKind === undefined) a.simulationKind = 'deposition';
    delete (a as any).modelCode;
  }

  /**
   * v2 → v3 migration: each area now owns its driverset. We pick a default to
   * snapshot into every loaded area:
   *   - If the project references a system, use the system's first driver set.
   *   - Otherwise, use the first driver set saved with the file (legacy
   *     project-level driversets array). May be null.
   * The file-level `driversets` array is dropped after migration.
   */
  const loadProjectV2 = async (json: any) => {
    const assets = await loadSystemAssets(json.project.systemUUID, json.layers || []);
    if(!assets.ok) return;

    const fallbackDriverset: IDriverset | null =
      assets.driversets[0]
        ?? (Array.isArray(json.driversets) && json.driversets[0] ? json.driversets[0] : null);

    setProject({...json.project});
    setDriversets(assets.driversets);
    setLayers(assets.layers);
    setStructures([...(json.structures || [])]);
    setReferences([...(json.references || [])]);
    const areas: IArea[] = (json.areas || []).map((a: any) => {
      applyAreaDefaults(a);
      // v2 areas have no driverset; snapshot the chosen fallback into every
      // area so each carries its own copy from this point on.
      a.driverset = fallbackDriverset
        ? { name: fallbackDriverset.name, dates: fallbackDriverset.dates, custom: false }
        : null;
      return a as IArea;
    });
    setAreas(areas);
    map.current?.flyTo({
      center: [json.map.longitude, json.map.latitude],
      zoom: json.map.zoom
    });
  }

  const loadProjectV3 = async (json: IFinsFile) => {
    const assets = await loadSystemAssets(json.project.systemUUID, json.layers || []);
    if(!assets.ok) return;

    setProject({...json.project});
    setDriversets(assets.driversets);
    setLayers(assets.layers);
    setStructures([...json.structures]);
    setReferences([...json.references]);
    json.areas.forEach(applyAreaDefaults);
    setAreas([...json.areas]);
    map.current?.flyTo({
      center: [json.map.longitude, json.map.latitude],
      zoom: json.map.zoom
    });
  }

  const parseLayers = (systemLayers: ISystemLayer[]) => {
    const newLayers = systemLayers.map(l => ({ ...LayerUtils.createLayer(l.type, l.name, l.data), visible: false }));
    // Find first bathymetry layer and make it visible.
    const b = newLayers.find(l => l.type == ELayerType.Depth);
    if(b) b.visible = true;
    // Find first current speed layer and make it visible.
    const v = newLayers.find(l => l.type == ELayerType.CurrentSpeed);
    if(v) v.visible = true;
    
    setLayers(newLayers);
    if(newLayers.length > 0) focusLayer(newLayers[0]);
    setMode(null);
  }

  const saveProject = () => {
    if(!map.current) return;
    const file: IFinsFile = {
      version: "3",
      map: {
        longitude: map.current.getCenter().lng,
        latitude: map.current.getCenter().lat,
        zoom: map.current.getZoom()
      },
      project, // include systemUUID for loading system layers
      // Don't save depth and current speed layers; these are part of the system.
      // UNLESS no system is loaded.
      layers: layers.filter(l => !project.systemUUID || (l.type != ELayerType.Depth && l.type != ELayerType.CurrentSpeed)),
      structures,
      references,
      areas
    }
    
    let blob = new Blob([JSON.stringify(file)], { type: "application/json; chartset=utf-8" });
    const filename = project.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    saveAs(blob, `${filename}.fins`);
  }

  const clearProject = () => {
    setProject({...DefaultProject});
    setLayers([]);
    setStructures([]);
    setReferences([]);
    setAreas([]);
    setSelectedArea(null);
    setSelectedStructures([]);
    setSelectedReference(null);
    setMode(null);
    if(map.current) map.current.flyTo({
      center: [0,0],
      zoom: 2
    });
  }

  // AREAS

  const sortAreas = (areas: IArea[]): IArea[] => {
    return areas.sort((a, b) => {
      return a.name.localeCompare(b.name);
    });    
  }
    
  const deleteArea = (area: IArea) => {
    // Remove area from array, creating new array so that components will update.
    setAreas([...areas.filter(a => a != area)]);
    setSelectedArea(null);
  }

  const clearAreas = () => {
    setSelectedArea(null);
    setAreas([]);
  }
  
  const addArea = (rect: IRect) => {
    // Snapshot the system's first ("default") driver set into the area, or null
    // if no system is loaded. Admin can override via DriverTab.
    const seedDriverset: IDriverset | null = driversets[0]
      ? { ...driversets[0], custom: false }
      : null;
    // Seed the culture period from the project's species, copied once rather
    // than read live: the user tunes days per area, and changing the project
    // species later must not silently rewrite periods they have set.
    const seedDays = speciesDefaultsFor(project, species).culturePeriod ?? 500;
    const area: IArea = {
      name: `Area ${areas.length+1}`,
      rect: rect,
      // Seeded from a target cell size rather than a fixed cell count, so a new
      // area's grid has the same physical scale however large it was drawn.
      resolution: AreaGridUtils.resolutionForCellSize(rect, AreaGridUtils.DEFAULT_CELL_SIZE).resolution,
      bathymetry: 'positive',
      demThreshold: project.clipping,
      days: seedDays,
      structureDepth: project.structureDepth,
      faecesDiameter: 3.3,
      feedPelletDiameter: 4.5,
      xDispersion: 0.05,
      yDispersion: 0.05,
      solver: 0,
      timestepReductionFactor: 1,
      simulationKind: 'deposition',
      driverset: seedDriverset,
    };
    setAreas(sortAreas([...areas, area]));
    setSelectedArea(area);
    setMode('area-editor');
  }

  const updateSelectedArea = (newRect: IRect) => {
    if(!selectedArea) return;
    selectedArea.rect.minLng = newRect.minLng;
    selectedArea.rect.maxLng = newRect.maxLng;
    selectedArea.rect.minLat = newRect.minLat;
    selectedArea.rect.maxLat = newRect.maxLat;
    // Reshaping the rectangle changes what the cell budget allows: dragging a
    // square area into a tall sliver multiplies the row count without touching
    // the resolution. Pull it back in so the cap holds however the area is
    // edited, not only when the cell size is typed.
    selectedArea.resolution = Math.min(
      selectedArea.resolution,
      AreaGridUtils.maxResolution(selectedArea.rect));
    setAreas(sortAreas([...areas]));
  }

  const updateArea = (area: IArea) => {
    // Update by creating new array of areas so that components update.
    setAreas(sortAreas([ ...areas.filter(a => a != area), area]));
  }

  const focusArea = (area: IArea) => {
    if(!map.current) return;
    map.current.fitBounds([
      [ area.rect.minLng, area.rect.minLat ],
      [ area.rect.maxLng, area.rect.maxLat ]
    ]);
  }
  
  const startOrganix = (area: IArea) => {
    setOrganix({ area, intent: 'simulate' });
  }

  const startOrganixExport = (area: IArea) => {
    setOrganix({ area, intent: 'export' });
  }

  const stopOrganix = () => {
    setOrganix(null);
  }

  const hasBathymetry = (): boolean => 
    layers.some(l => l.type == ELayerType.Depth);

  const hasCurrentSpeed = (): boolean => 
    layers.some(l => l.type == ELayerType.CurrentSpeed);

  //
  // SPECIES
  //
  const [speciesReady, setSpeciesReady] = React.useState<TResourceState>('loading');
  const [species, setSpecies] = React.useState<ISpecies[]>([]);

  const loadSpecies = () => {
    setSpecies([]);
    setSpeciesReady('loading');
    if(auth.isAuthenticated) {
      SpeciesApi.list()
      .then((res) => {
        setSpecies(res);
        setSpeciesReady('ready');
      })
      .catch(() => {
        setSpeciesReady('failed');
       });
    }
  }

  React.useEffect(() => {
    loadSpecies();
  }, [auth.isAuthenticated]);

  // Clear project when user signs in: 
  /* React.useEffect(() => {
    const now = auth.isAuthenticated;
    const before = wasAuthenticated.current;

    // Only run on actual sign-in transition:
    if (!before && now) {
      clearProject();
    } 

    wasAuthenticated.current = now;
  }, [auth.isAuthenticated]); */

  //
  // Create species.
  //
  const createSpecies = async(newSpecies: ISpecies): Promise<ISpecies> => {
    const result = await SpeciesApi.create(newSpecies)
      .then((s) => {
        setSpecies([...species, s]);
        toaster.toast("Species created.", { dismissable: true });
        return s;
      })
      .catch((e:any): any => {
        toaster.toast("Failed to save species.", { dismissable: true });
        return null;
      });
    return result;
  }

  //
  // Update specified species with new values.
  //
  const updateSpecies = async (oldSpecies: ISpecies, values: ISpecies): Promise<boolean> => {
    const result = await SpeciesApi.update(values)
      .then((s) => {
        Object.assign(oldSpecies, s);
        setSpecies([...species.filter(s => s.uuid != oldSpecies.uuid), oldSpecies]);
        toaster.toast("Species updated.", { dismissable: true });
        return s;
      })
      .catch((e:any): any => {
        toaster.toast("Failed to update species.", { dismissable: true });
        return null;
      });
    return result != null;
  }

  //
  // Delete specified species:
  //
  const deleteSpecies = async (speciesToDelete: ISpecies): Promise<boolean> => {
    const result = await SpeciesApi.delete(speciesToDelete.uuid)
      .then(() => {
        // Remove species from species collection.
        setSpecies([...species.filter(s => s.uuid != speciesToDelete.uuid)]);
        toaster.toast("Species deleted.", { dismissable: true });
        return true;
      })
      .catch((e:any): any => {
        toaster.toast("Failed to delete species.", { dismissable: true });
        return null;
      });
    return result != null;
  }    

  return (
    <GlobalStateContext.Provider value={{
      guide,
      setGuide,
      layers,
      structures,
      setStructures,
      references, setReferences,
      project, setProject,
      selectedReference,
      setSelectedReference,
      setMap,
      getMap,
      startImport,
      startReferenceImport,
      addLayers,
      toggleLayer,
      activeLayer,
      setActiveLayer,
      addResultLayers,
      deleteRun,
      deleteLayer,
      focusLayer,
      updateLayer,
      clearLayers,
      mode,
      setMode,
      expanded,
      setPaneExpansion,
      togglePaneExpansion,
      showStructures,
      setShowStructures,
      addStructure,
      addStructureCircle,
      addStructurePolygon,
      focusStructure,
      startEditStructure,
      clearStructures,

      // Multi-select structures:
      selectedStructures,
      setSelectedStructures,
      toggleStructureSelection,
      selectAllStructures,
      clearStructureSelection,
      deleteSelectedStructures,
      moveSelectedStructures,
      updateSelectedStructureCircle,
      updateSelectedStructurePolygon,

      clearReferences,
      updateReference,
      deleteReference,
      focusReference,
      toggleReference,
      startLoadProject,
      loadProject,
      parseLayers,
      clearProject,
      saveProject,
      areas,
      setAreas,
      selectedArea,
      setSelectedArea,
      deleteArea,
      clearAreas,
      addArea,
      updateSelectedArea,
      updateArea,
      focusArea,
      startOrganix,
      startOrganixExport,
      stopOrganix,
      organix,
      hasBathymetry, hasCurrentSpeed,

      driversets, setDriversets,

      speciesReady, species, loadSpecies, createSpecies, updateSpecies, deleteSpecies,
    }}>
      {props.children}
      {importingLayer && <ExcelImport onCancel={() => setImportingLayer(false)} onImport={handleImport} />}
      {referenceImporting && <ReferenceImport onCancel={() => setReferenceImporting(false)} onImport={handleReferenceImport}/>}
      {editingStructure && <EditStructureDialog structure={editingStructure} onCancel={cancelEditStructure} onSubmit={updateSelectedStructure}/>}
      {loadingProject && <LoadProjectDialog onCancel={() => setLoadingProject(false)} onImport={() => setLoadingProject(false)}/>}
      {/* {exportingOrganix && <OrganixExportDialog area={exportingOrganix} onClose={() => setExportingOrganix(null)}/>} */}
    </GlobalStateContext.Provider>
  )
}

export { GlobalState }
